/**
 * WebGL Accelerator for GPU-accelerated Color Transformations
 * Provides hardware acceleration for matrix operations and color calculations
 */

import type { ColorTransformMatrix } from '../../types/substrate';
import type { CMYK } from '../colorEngine';

export interface WebGLCapabilities {
  supported: boolean;
  maxTextureSize: number;
  maxVertexUniforms: number;
  maxFragmentUniforms: number;
  vendor: string;
  renderer: string;
}

export interface PerformanceMetrics {
  gpuCalculations: number;
  cpuFallbacks: number;
  averageGPUTime: number;
  averageCPUTime: number;
  memoryUsage: number;
}

/**
 * WebGL Accelerator class for GPU-accelerated color transformations
 */
export class WebGLAccelerator {
  private gl: WebGLRenderingContext | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private capabilities: WebGLCapabilities;
  private metrics: PerformanceMetrics;
  private isInitialized = false;
  private shaderProgram: WebGLProgram | null = null;
  private positionBuffer: WebGLBuffer | null = null;
  private texCoordBuffer: WebGLBuffer | null = null;
  private outputTexture: WebGLTexture | null = null;
  private framebuffer: WebGLFramebuffer | null = null;

  constructor() {
    this.capabilities = this.detectCapabilities();
    this.metrics = {
      gpuCalculations: 0,
      cpuFallbacks: 0,
      averageGPUTime: 0,
      averageCPUTime: 0,
      memoryUsage: 0
    };
  }

  /**
   * Initialize WebGL context and shaders
   */
  initialize(): boolean {
    if (this.isInitialized) {
      return this.capabilities.supported;
    }

    if (!this.capabilities.supported) {
      console.warn('WebGL not supported, will use CPU fallback');
      return false;
    }

    try {
      // Create WebGL context (reuse single canvas)
      this.canvas = document.createElement('canvas');
      this.canvas.width = 1;
      this.canvas.height = 1;
      this.gl = (this.canvas.getContext('webgl') || this.canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;

      if (!this.gl) {
        console.warn('WebGL context creation failed');
        return false;
      }

      // Initialize reusable resources
      this.initializeResources();
      this.isInitialized = true;
      
      console.log('WebGL accelerator initialized:', this.capabilities);
      return true;
    } catch (error) {
      console.error('WebGL initialization failed:', error);
      return false;
    }
  }

  /**
   * Detect WebGL capabilities
   */
  private detectCapabilities(): WebGLCapabilities {
    // Check if we're in a browser environment with document support
    if (typeof document === 'undefined') {
      return {
        supported: false,
        maxTextureSize: 0,
        maxVertexUniforms: 0,
        maxFragmentUniforms: 0,
        vendor: 'SSR/Node Environment',
        renderer: 'Not Available'
      };
    }

    const canvas = document.createElement('canvas');
    const gl = (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;

    if (!gl) {
      return {
        supported: false,
        maxTextureSize: 0,
        maxVertexUniforms: 0,
        maxFragmentUniforms: 0,
        vendor: 'Unknown',
        renderer: 'Unknown'
      };
    }

    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    
    return {
      supported: true,
      maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
      maxVertexUniforms: gl.getParameter(gl.MAX_VERTEX_UNIFORM_VECTORS),
      maxFragmentUniforms: gl.getParameter(gl.MAX_FRAGMENT_UNIFORM_VECTORS),
      vendor: debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : 'Unknown',
      renderer: debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : 'Unknown'
    };
  }

  /**
   * Initialize reusable WebGL resources
   */
  private initializeResources(): void {
    if (!this.gl) return;

    // Vertex shader (passthrough)
    const vertexShaderSource = `
      attribute vec2 a_position;
      attribute vec2 a_texCoord;
      varying vec2 v_texCoord;
      
      void main() {
        gl_Position = vec4(a_position, 0.0, 1.0);
        v_texCoord = a_texCoord;
      }
    `;

    // Fragment shader for 4x5 matrix transformation
    const fragmentShaderSource = `
      precision mediump float;
      
      uniform vec4 u_cmyk;        // Input CMYK values
      uniform mat4 u_transform;   // 4x4 part of transformation matrix
      uniform vec4 u_offset;      // 5th column of transformation matrix
      
      varying vec2 v_texCoord;
      
      void main() {
        // Apply 4x5 transformation: output = transform * input + offset
        vec4 input = vec4(u_cmyk.rgb, u_cmyk.a);
        vec4 output = u_transform * input + u_offset;
        
        // Clamp to valid range and output
        gl_FragColor = vec4(
          clamp(output.r, 0.0, 1.0),
          clamp(output.g, 0.0, 1.0),
          clamp(output.b, 0.0, 1.0),
          clamp(output.a, 0.0, 1.0)
        );
      }
    `;

    // Compile shaders
    const vertexShader = this.compileShader(vertexShaderSource, this.gl.VERTEX_SHADER);
    const fragmentShader = this.compileShader(fragmentShaderSource, this.gl.FRAGMENT_SHADER);

    if (vertexShader && fragmentShader) {
      // Create program
      const program = this.gl.createProgram();
      if (program) {
        this.gl.attachShader(program, vertexShader);
        this.gl.attachShader(program, fragmentShader);
        this.gl.linkProgram(program);

        if (this.gl.getProgramParameter(program, this.gl.LINK_STATUS)) {
          this.shaderProgram = program;
          this.initializeBuffers();
        } else {
          console.error('Shader program linking failed:', this.gl.getProgramInfoLog(program));
        }
      }
    }
  }

  /**
   * Initialize reusable buffers and textures
   */
  private initializeBuffers(): void {
    if (!this.gl || !this.shaderProgram) return;

    // Set up geometry (single quad) - reusable
    const positions = new Float32Array([
      -1, -1,
       1, -1,
      -1,  1,
       1,  1,
    ]);

    const texCoords = new Float32Array([
      0, 0,
      1, 0,
      0, 1,
      1, 1,
    ]);

    // Create and bind position buffer
    this.positionBuffer = this.gl.createBuffer();
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.positionBuffer);
    this.gl.bufferData(this.gl.ARRAY_BUFFER, positions, this.gl.STATIC_DRAW);

    const positionLocation = this.gl.getAttribLocation(this.shaderProgram, 'a_position');
    this.gl.enableVertexAttribArray(positionLocation);
    this.gl.vertexAttribPointer(positionLocation, 2, this.gl.FLOAT, false, 0, 0);

    // Create and bind tex coord buffer
    this.texCoordBuffer = this.gl.createBuffer();
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.texCoordBuffer);
    this.gl.bufferData(this.gl.ARRAY_BUFFER, texCoords, this.gl.STATIC_DRAW);

    const texCoordLocation = this.gl.getAttribLocation(this.shaderProgram, 'a_texCoord');
    this.gl.enableVertexAttribArray(texCoordLocation);
    this.gl.vertexAttribPointer(texCoordLocation, 2, this.gl.FLOAT, false, 0, 0);

    // Create output texture and framebuffer - reusable
    this.outputTexture = this.gl.createTexture();
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.outputTexture);
    this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.gl.RGBA, 1, 1, 0, this.gl.RGBA, this.gl.UNSIGNED_BYTE, null);
    this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.NEAREST);
    this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.NEAREST);

    this.framebuffer = this.gl.createFramebuffer();
    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.framebuffer);
    this.gl.framebufferTexture2D(this.gl.FRAMEBUFFER, this.gl.COLOR_ATTACHMENT0, this.gl.TEXTURE_2D, this.outputTexture, 0);
  }

  /**
   * Compile a shader
   */
  private compileShader(source: string, type: number): WebGLShader | null {
    if (!this.gl) return null;

    const shader = this.gl.createShader(type);
    if (!shader) return null;

    this.gl.shaderSource(shader, source);
    this.gl.compileShader(shader);

    if (this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS)) {
      return shader;
    } else {
      console.error('Shader compilation failed:', this.gl.getShaderInfoLog(shader));
      this.gl.deleteShader(shader);
      return null;
    }
  }

  /**
   * Apply color transformation using WebGL
   */
  applyColorTransformGPU(cmyk: CMYK, transform: ColorTransformMatrix): CMYK {
    const startTime = performance.now();

    if (!this.isInitialized || !this.gl || !this.shaderProgram) {
      return this.applyColorTransformCPU(cmyk, transform);
    }

    try {
      // Use shader program
      this.gl.useProgram(this.shaderProgram);

      // Set uniforms
      const cmykLocation = this.gl.getUniformLocation(this.shaderProgram, 'u_cmyk');
      this.gl.uniform4f(cmykLocation, 
        cmyk.c / 100, 
        cmyk.m / 100, 
        cmyk.y / 100, 
        cmyk.k / 100
      );

      // Convert 4x5 matrix to 4x4 + offset for WebGL
      const matrixLocation = this.gl.getUniformLocation(this.shaderProgram, 'u_transform');
      const matrix4x4 = new Float32Array([
        transform.matrix[0][0], transform.matrix[0][1], transform.matrix[0][2], transform.matrix[0][3],
        transform.matrix[1][0], transform.matrix[1][1], transform.matrix[1][2], transform.matrix[1][3],
        transform.matrix[2][0], transform.matrix[2][1], transform.matrix[2][2], transform.matrix[2][3],
        transform.matrix[3][0], transform.matrix[3][1], transform.matrix[3][2], transform.matrix[3][3],
      ]);
      this.gl.uniformMatrix4fv(matrixLocation, false, matrix4x4);

      const offsetLocation = this.gl.getUniformLocation(this.shaderProgram, 'u_offset');
      this.gl.uniform4f(offsetLocation,
        transform.matrix[0][4],
        transform.matrix[1][4],
        transform.matrix[2][4],
        transform.matrix[3][4]
      );

      // Bind framebuffer for rendering
      this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.framebuffer);

      // Render
      this.gl.viewport(0, 0, 1, 1);
      this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);

      // Read results
      const pixels = new Uint8Array(4);
      this.gl.readPixels(0, 0, 1, 1, this.gl.RGBA, this.gl.UNSIGNED_BYTE, pixels);

      // Update metrics
      const endTime = performance.now();
      this.metrics.gpuCalculations++;
      this.metrics.averageGPUTime = (this.metrics.averageGPUTime * (this.metrics.gpuCalculations - 1) + (endTime - startTime)) / this.metrics.gpuCalculations;

      // Convert back to CMYK
      return {
        c: Math.max(0, Math.min(100, (pixels[0] / 255) * 100)),
        m: Math.max(0, Math.min(100, (pixels[1] / 255) * 100)),
        y: Math.max(0, Math.min(100, (pixels[2] / 255) * 100)),
        k: Math.max(0, Math.min(100, (pixels[3] / 255) * 100))
      };

    } catch (error) {
      console.error('GPU calculation failed, falling back to CPU:', error);
      return this.applyColorTransformCPU(cmyk, transform);
    }
  }

  /**
   * CPU fallback for color transformations
   */
  private applyColorTransformCPU(cmyk: CMYK, transform: ColorTransformMatrix): CMYK {
    const startTime = performance.now();

    const input = [cmyk.c / 100, cmyk.m / 100, cmyk.y / 100, cmyk.k / 100, 1];
    const output = [0, 0, 0, 0];

    // Apply 4x5 transformation matrix
    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 5; j++) {
        output[i] += transform.matrix[i][j] * input[j];
      }
    }

    // Update metrics
    const endTime = performance.now();
    this.metrics.cpuFallbacks++;
    this.metrics.averageCPUTime = (this.metrics.averageCPUTime * (this.metrics.cpuFallbacks - 1) + (endTime - startTime)) / this.metrics.cpuFallbacks;

    return {
      c: Math.max(0, Math.min(100, output[0] * 100)),
      m: Math.max(0, Math.min(100, output[1] * 100)),
      y: Math.max(0, Math.min(100, output[2] * 100)),
      k: Math.max(0, Math.min(100, output[3] * 100))
    };
  }

  /**
   * Batch process multiple color transformations
   */
  batchTransform(colors: CMYK[], transform: ColorTransformMatrix): CMYK[] {
    if (!this.isInitialized || colors.length < 50) {
      // Use CPU for small batches
      return colors.map(cmyk => this.applyColorTransformCPU(cmyk, transform));
    }

    // Use GPU for larger batches
    return colors.map(cmyk => this.applyColorTransformGPU(cmyk, transform));
  }

  /**
   * Get WebGL capabilities
   */
  getCapabilities(): WebGLCapabilities {
    return this.capabilities;
  }

  /**
   * Get performance metrics
   */
  getMetrics(): PerformanceMetrics {
    return { ...this.metrics };
  }

  /**
   * Check if WebGL is available and performing well
   */
  shouldUseGPU(batchSize: number = 1): boolean {
    if (!this.isInitialized || !this.capabilities.supported) {
      return false;
    }

    // Use GPU for larger batches or if GPU is proven faster
    if (batchSize >= 50) {
      return true;
    }

    // Use GPU if we have enough calculations to justify overhead
    const totalCalculations = this.metrics.gpuCalculations + this.metrics.cpuFallbacks;
    if (totalCalculations < 10) {
      return false; // Not enough data to decide
    }

    // Use GPU if it's faster on average
    return this.metrics.averageGPUTime < this.metrics.averageCPUTime * 0.8; // 20% tolerance
  }

  /**
   * Dispose of WebGL resources
   */
  dispose(): void {
    if (this.gl) {
      // Clean up shader program
      if (this.shaderProgram) {
        this.gl.deleteProgram(this.shaderProgram);
        this.shaderProgram = null;
      }

      // Clean up buffers and textures
      if (this.positionBuffer) {
        this.gl.deleteBuffer(this.positionBuffer);
        this.positionBuffer = null;
      }
      if (this.texCoordBuffer) {
        this.gl.deleteBuffer(this.texCoordBuffer);
        this.texCoordBuffer = null;
      }
      if (this.outputTexture) {
        this.gl.deleteTexture(this.outputTexture);
        this.outputTexture = null;
      }
      if (this.framebuffer) {
        this.gl.deleteFramebuffer(this.framebuffer);
        this.framebuffer = null;
      }

      // Lose context (optional)
      const loseContext = this.gl.getExtension('WEBGL_lose_context');
      if (loseContext) {
        loseContext.loseContext();
      }

      this.gl = null;
    }

    this.canvas = null;
    this.isInitialized = false;
  }

  /**
   * Reset performance metrics
   */
  resetMetrics(): void {
    this.metrics = {
      gpuCalculations: 0,
      cpuFallbacks: 0,
      averageGPUTime: 0,
      averageCPUTime: 0,
      memoryUsage: 0
    };
  }
}

// Global WebGL accelerator instance
export const webglAccelerator = new WebGLAccelerator();
